// ProgramBakeoffCard — a compact tile that runs the borrower's income through
// all 5 agency guidelines (Fannie / Freddie / FHA / VA / USDA) and shows the
// winning program with a delta vs the loan's currently-selected program.
//
// Data source: the borrower's current URLA employment rows. W-2 rows are summed into
// an annual base; self-employed and other-income rows need the Income Calculator, so
// they are left out of the comparison and listed in the footer.
// Endpoint: POST /api/income/calculate-all-programs
import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../../api';
import { Link } from 'react-router-dom';
import { Trophy, Sparkles, ArrowRight, Info } from 'lucide-react';
import { fmt$ } from '../shared/formFields';
import { splitBakeoffIncome } from '../shared/bakeoffIncome';

const AGENCY_STYLE = {
  fannie:  { color: '#065F46', bg: '#ECFDF5', short: 'FNMA',  label: 'Fannie Mae' },
  freddie: { color: '#1E40AF', bg: '#EFF6FF', short: 'FHLMC', label: 'Freddie Mac' },
  fha:     { color: '#9A3412', bg: '#FFF7ED', short: 'FHA',   label: 'FHA' },
  va:      { color: '#7C2D12', bg: '#FEF3C7', short: 'VA',    label: 'VA' },
  usda:    { color: '#166534', bg: '#F0FDF4', short: 'USDA',  label: 'USDA' },
};

// Map loan.loan_program → the corresponding key used by the compare API.
// Conventional loans compete under Fannie/Freddie; we tag both as "current"
// so the user sees the current guideline highlighted regardless.
function currentAgencyKey(loanProgram) {
  return {
    conventional: 'fannie',
    fannie: 'fannie',
    freddie: 'freddie',
    fha: 'fha',
    va: 'va',
    usda: 'usda',
  }[(loanProgram || '').toLowerCase()] || null;
}

export function ProgramBakeoffCard({ loan }) {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const { monthlyIncome, annualBase, otherMonthly, otherRows } = useMemo(
    () => splitBakeoffIncome(loan.urla?.current_employment),
    [loan],
  );
  const otherNote = otherRows > 0
    ? `${otherRows} self-employed or other-income ${otherRows === 1 ? 'row' : 'rows'} (${fmt$(otherMonthly)}/mo stated) left out`
    : null;

  const currentKey = currentAgencyKey(loan.loan_program);

  // Auto-run the comparison whenever the input changes.
  useEffect(() => {
    if (!annualBase || annualBase <= 0) { setData(null); return; }
    let stop = false;
    setBusy(true);
    setError(null);
    api.post('/income/calculate-all-programs', {
      income_type: 'w2_salary',
      y2_gross: annualBase,
    })
      .then(({ data }) => { if (!stop) setData(data); })
      .catch((e) => { if (!stop) setError(e.response?.data?.detail || 'Comparison failed'); })
      .finally(() => { if (!stop) setBusy(false); });
    return () => { stop = true; };
  }, [annualBase]);

  // Empty state — no income captured yet
  if (!annualBase || annualBase <= 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="program-bakeoff-card">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #6BA82E 0%, #8bc94f 100%)' }}>
            <Trophy size={13} className="text-white" />
          </div>
          <div className="text-[13px] font-semibold" style={{ color: '#030524' }}>Program Bakeoff</div>
        </div>
        <div className="text-[12px] text-slate-500 leading-relaxed">
          Add borrower employment via the <b>URLA (Full 1003)</b> module (Current Employment)
          to see which loan program gives this borrower the highest qualifying income.
        </div>
        {otherNote && (
          <div className="text-[11px] text-slate-500 mt-2" data-testid="bakeoff-other-note">
            {otherNote}: the W-2 rules can't price them. Use the{' '}
            <Link to="/portal/income" className="underline">Income Calculator</Link>.
          </div>
        )}
      </div>
    );
  }

  if (busy && !data) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5 animate-pulse" data-testid="program-bakeoff-card">
        <div className="text-[13px] font-semibold text-slate-400">Running Program Bakeoff…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="program-bakeoff-card">
        <div className="text-[13px] font-semibold text-red-600">Program Bakeoff — {error}</div>
      </div>
    );
  }

  if (!data) return null;

  const bestKey = data.best_program;
  const bestQm = data.best_qualifying_monthly || 0;
  const bestStyle = AGENCY_STYLE[bestKey] || AGENCY_STYLE.fannie;

  const currentRow = currentKey ? data.results.find(r => r.program === currentKey) : null;
  const currentQm = currentRow?.qualifying_monthly || 0;
  const currentDelta = bestQm - currentQm;
  const currentIsBest = currentKey === bestKey;

  // Sort results by qualifying_monthly desc
  const sortedResults = [...data.results].sort((a, b) => b.qualifying_monthly - a.qualifying_monthly);

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="program-bakeoff-card">
      {/* Header banner */}
      <div className="px-5 py-3.5 flex items-center gap-3" style={{ background: `linear-gradient(135deg, ${bestStyle.bg} 0%, #ffffff 100%)`, borderBottom: `1px solid ${bestStyle.color}22` }}>
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: bestStyle.color }}>
          <Trophy size={16} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: bestStyle.color }}>
            Best Program
          </div>
          <div className="font-serif text-lg font-bold truncate" style={{ color: '#030524' }} data-testid="bakeoff-winner-label">
            {bestKey ? bestStyle.label : 'No program yet'}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase text-slate-500 font-bold">Qualifying /mo</div>
          <div className="font-serif text-2xl font-bold" style={{ color: bestStyle.color }} data-testid="bakeoff-winner-amount">
            {data.best_qualifying_monthly == null ? '—' : fmt$(bestQm)}
          </div>
        </div>
      </div>

      {/* Delta pill vs current loan program */}
      {currentRow && bestKey && (
        <div className="px-5 py-2.5 flex items-center gap-2 border-b border-slate-100 text-[12px]">
          {currentIsBest ? (
            <>
              <Sparkles size={12} style={{ color: '#6BA82E' }} />
              <span className="text-slate-600">
                Current loan is on the <b style={{ color: bestStyle.color }}>best</b> program — nice work.
              </span>
            </>
          ) : (
            <>
              <ArrowRight size={12} className="text-amber-600" />
              <span className="text-slate-600">
                Switching to <b style={{ color: bestStyle.color }}>{bestStyle.label}</b> would gain{' '}
                <b className="text-emerald-700" data-testid="bakeoff-delta">{fmt$(currentDelta)}/mo</b>{' '}
                <span className="text-slate-400">({AGENCY_STYLE[currentKey]?.short || currentKey?.toUpperCase()} → {bestStyle.short})</span>
              </span>
            </>
          )}
        </div>
      )}

      {/* Compact per-agency list */}
      <div className="divide-y divide-slate-100">
        {sortedResults.map((r) => {
          const s = AGENCY_STYLE[r.program] || {};
          const isBest = r.program === bestKey;
          const isCurrent = r.program === currentKey;
          const deltaFromBest = bestQm - r.qualifying_monthly;
          return (
            <div key={r.program} className="px-5 py-2 flex items-center gap-3 text-[12px]" data-testid={`bakeoff-row-${r.program}`}>
              <span className="w-14 text-center py-0.5 rounded font-bold text-[10px]" style={{ background: s.bg, color: s.color }}>
                {s.short}
              </span>
              <span className="flex-1 min-w-0 truncate text-slate-700">
                {s.label || r.program}
                {isBest && <span className="ml-1.5 text-[9px] font-bold px-1 py-0.5 rounded" style={{ background: '#DCFCE7', color: '#166534' }}>BEST</span>}
                {isCurrent && !isBest && <span className="ml-1.5 text-[9px] font-bold px-1 py-0.5 rounded" style={{ background: '#EEF2FF', color: '#4338CA' }}>CURRENT</span>}
              </span>
              <span className="font-semibold shrink-0" style={{ color: isBest ? s.color : '#030524' }}>
                {fmt$(r.qualifying_monthly)}
              </span>
              {!isBest && deltaFromBest > 0 && (
                <span className="text-[10px] font-mono text-red-500 shrink-0 w-16 text-right">
                  −{fmt$(deltaFromBest)}
                </span>
              )}
              {isBest && <span className="w-16" />}
            </div>
          );
        })}
      </div>

      {/* Footer — deep link */}
      <div className="px-5 py-2.5 flex items-center justify-between gap-2 border-t border-slate-100 bg-slate-50">
        <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
          <Info size={11} />
          <span data-testid="bakeoff-basis">
            Based on {fmt$(monthlyIncome)}/mo current W-2 income · W-2 salary rules
            {otherNote && <> · <span data-testid="bakeoff-other-note">{otherNote}</span></>}
          </span>
        </div>
        <Link
          to={`/portal/income?loan_type=${bestKey}`}
          data-testid="bakeoff-open-full"
          className="text-[11px] font-semibold hover:underline inline-flex items-center gap-1"
          style={{ color: '#070B5F' }}
        >
          Full comparison <ArrowRight size={10} />
        </Link>
      </div>
    </div>
  );
}
